import {
  Heading,
  Text,
  Box,
  Flex,
  VStack,
  StackDivider,
  StackProps,
  HeadingProps,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
} from '@chakra-ui/react';
import { FC } from 'react';
import {
  ComputedDataItem,
  ComputedDataSection,
} from '@/lib/propertyInfo/types';

export const ListingDetailsItem: React.FC<{ title?: string }> = ({
  title,
  children,
}) => (
  <AccordionItem>
    <AccordionButton
      px={0}
      py={5}
      justifyContent="space-between"
      _hover={{ background: 'transparent' }}
      sx={{
        '@media print': {
          pb: 2,
          svg: { display: 'none' },
        },
      }}
    >
      <Heading size="sm">{title}</Heading>
      <AccordionIcon />
    </AccordionButton>
    <AccordionPanel px={0} py={5}>
      {children}
    </AccordionPanel>
  </AccordionItem>
);

export const SubHeading: React.FC<HeadingProps> = ({
  children,
  ...headingProps
}) => (
  <Box className="sub-heading" pt={5} mb={5}>
    <Heading size="sm" color="gray.800" width="100%" py={2} {...headingProps}>
      {children}
    </Heading>
  </Box>
);

export const CustomStack: React.FC<StackProps> = ({
  children,
  ...stackProps
}) => (
  <VStack
    align="flex-start"
    spacing={4}
    divider={<StackDivider borderColor="gray.100" className="stack-divider" />}
    sx={{
      '.stack-divider:first-child, .stack-divider + .stack-divider, .sub-heading + .stack-divider':
        {
          display: 'none',
        },
      '.sub-stack .detail-item-label': { paddingLeft: '1em' },
    }}
    {...stackProps}
  >
    {children}
  </VStack>
);

export const DetailItem: React.FC<{ item: ComputedDataItem }> = ({
  item: { description, label, value },
  children,
}) => (
  <>
    <Flex flex="1" flexDir="row" width="100%">
      <Box width="50%" pr={4}>
        <Text
          fontWeight="600"
          fontSize="sm"
          sx={{ '@media print': { fontWeight: '400' } }}
          className="detail-item-label"
        >
          {label}
        </Text>
      </Box>
      <Box width="50%">
        <Text fontWeight="500" fontSize="sm" whiteSpace="pre-line">
          {value}
        </Text>
        {description && (
          <Text whiteSpace="pre-line" color="gray.600" fontSize="sm" mb={1}>
            {description}
          </Text>
        )}
      </Box>
    </Flex>
    {/* Render a sub stack of children if we got any and no-one has a hidden prop set */}
    {children && (
      <CustomStack
        align="flex-start"
        width="100%"
        spacing={2}
        paddingTop={2}
        divider={undefined}
        className="sub-stack"
      >
        {children}
      </CustomStack>
    )}
  </>
);

/**
 * This could be a component instead of function, but since the DetailItem
 * components need to be direct descendants of the CustomStack, we can't
 * have an intermediate component rendering those DetailItem components, nor
 * can we have a Fragment wrapping the title and items.
 * Instead we return an array of Elements.
 */
export const getDetailItems = ({ title, items }: ComputedDataSection) => {
  const rows = items.map(({ items: subItems, ...item }) => (
    <DetailItem item={item} key={item.name}>
      {subItems?.length
        ? subItems.map((subItem, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <DetailItem key={idx} item={subItem} />
          ))
        : null}
    </DetailItem>
  ));
  if (title) {
    rows.unshift(<SubHeading>{title}</SubHeading>);
  }
  return rows.filter((v: JSX.Element | null): v is JSX.Element => !!v);
};

export const InfoPanel: FC<{
  title?: string;
  sections?: (ComputedDataSection | undefined)[];
}> = ({ title, sections, children }) => {
  if (!sections) {
    return (
      <ListingDetailsItem title={title}>
        <CustomStack>{children}</CustomStack>
      </ListingDetailsItem>
    );
  }

  const nonEmptySections = sections.filter(
    (s: ComputedDataSection | undefined): s is ComputedDataSection => !!s,
  );

  if (!nonEmptySections.length) {
    return null;
  }

  return (
    // lift the title of the first section to the accordion heading
    <ListingDetailsItem title={nonEmptySections[0].title}>
      <CustomStack>
        {nonEmptySections.map((section, index) =>
          getDetailItems({
            ...section,
            // skip section title for first item, since it's
            // lifted to the accordion title above
            title: index ? section.title : undefined,
          }),
        )}
      </CustomStack>
    </ListingDetailsItem>
  );
};
